// src/hooks/usePremium.js — v275 (G4.23a)
//
// Canonical hook for checking if the current user has an active premium subscription.
// Single source of truth — replaces 9+ duplicate inline checks across the codebase.
//
// Background:
//   The `user.plan === 'premium'` legacy pattern was previously checked across many
//   components. After deep investigation (G4.23a Rule F audit), it was determined:
//   - Backend API ALWAYS returns user.plan as the subscription tier ID
//     ('free', '1d', '1m', '3m', '12m') — NEVER as the literal 'premium'
//   - The DB column `users.plan` may hold 'premium' but is NOT exposed to frontend
//   - The canonical truth source for "is user premium right now?" is:
//       user.subscription_expires_at AND it's in the future
//
// Usage:
//   import { usePremium } from '../hooks/usePremium';
//   ...
//   const { isPremium, planId } = usePremium();
//
// Reference: G4.23a session — refactor of 12 inline check sites.

import { useApp } from '../context/AppContext';

/**
 * Returns { isPremium, planId } for the current user.
 *
 * @returns {{ isPremium: boolean, planId: string }}
 *   - isPremium: true if user has an unexpired subscription
 *   - planId: the active subscription tier ID, e.g. '12m' / '3m' / '1m' / '1d' / 'free'
 *
 * Note: planId derivation matches the canonical fallback chain established in
 * useFeature.js:145 — `user?.plan || (isPremium ? '1m' : 'free')`. The '1m'
 * fallback is defensive for the edge case where subscription_expires_at is set
 * but user.plan is null/undefined (impossible per current backend, but kept for
 * safety per Rule G — see G4.23b changelog for the audit that locked this in).
 */
export function usePremium() {
  const { user } = useApp();
  const isPremium = !!(
    user?.subscription_expires_at &&
    new Date(user.subscription_expires_at) > new Date()
  );
  // Canonical planId fallback chain (matches useFeature.js:145 + mobile WatchlistScreen.js).
  const planId = user?.plan || (isPremium ? '1m' : 'free');
  return { isPremium, planId };
}

export default usePremium;
